import React, { useState, useEffect, useRef, useCallback, useTransition } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Sparkles,
  Code,
  Palette,
  TrendingUp,
  Building,
  Lightbulb,
  Loader,
  ChevronRight,
  Globe,
  Zap,
  BarChart3,
  BookOpen,
  XIcon,
  Download,
  CheckCircle
} from 'lucide-react';
import { apiClient } from '../services/api';
import { supabase } from '../lib/supabase';
import { useTheme } from '../App';
import { cn } from '../lib/utils';
import CodeBlock from '../components/CodeBlock';

const BuildersStudio = () => {
  const { t, i18n } = useTranslation();
  const { darkMode } = useTheme();
  const { recordAIInteraction, user, setUser } = useUser();
  const [shouldDeductCredit, setShouldDeductCredit] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Countries list for Ideas Generator
  const countries = [
    'All Countries',
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands', 
    'Sweden', 'Norway', 'Denmark', 'Finland', 'Switzerland', 'Austria', 'Belgium', 'Portugal', 'Ireland',
    'Australia', 'New Zealand', 'Japan', 'South Korea', 'Singapore', 'Hong Kong', 'Taiwan',
    'China', 'India', 'Indonesia', 'Thailand', 'Malaysia', 'Philippines', 'Vietnam',
    'Brazil', 'Argentina', 'Mexico', 'Chile', 'Colombia', 'Peru',
    'South Africa', 'Nigeria', 'Kenya', 'Egypt', 'Morocco',
    'UAE', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Jordan', 'Lebanon',
    'Turkey', 'Israel', 'Poland', 'Czech Republic', 'Hungary', 'Romania'
  ];

  // Arabic translations for countries
  const countriesArabic = [
    'جميع البلدان',
    'الولايات المتحدة', 'كندا', 'المملكة المتحدة', 'ألمانيا', 'فرنسا', 'إسبانيا', 'إيطاليا', 'هولندا',
    'السويد', 'النرويج', 'الدنمارك', 'فنلندا', 'سويسرا', 'النمسا', 'بلجيكا', 'البرتغال', 'أيرلندا',
    'أستراليا', 'نيوزيلندا', 'اليابان', 'كوريا الجنوبية', 'سنغافورة', 'هونغ كونغ', 'تايوان',
    'الصين', 'الهند', 'إندونيسيا', 'تايلاند', 'ماليزيا', 'الفلبين', 'فيتنام',
    'البرازيل', 'الأرجنتين', 'المكسيك', 'تشيلي', 'كولومبيا', 'بيرو',
    'جنوب أفريقيا', 'نيجيريا', 'كينيا', 'مصر', 'المغرب',
    'الإمارات العربية المتحدة', 'المملكة العربية السعودية', 'قطر', 'الكويت', 'البحرين', 'عُمان', 'الأردن', 'لبنان',
    'تركيا', 'إسرائيل', 'بولندا', 'جمهورية التشيك', 'المجر', 'رومانيا'
  ];

  // Get countries list based on current language
  const getCountriesList = () => i18n.language === 'ar' ? countriesArabic : countries;

  // Complexity options
  const complexityOptions = ['Easy', 'Moderate', 'Complex', 'All'];
  const complexityOptionsArabic = ['سهل', 'متوسط', 'معقد', 'الكل'];
  
  // Get complexity options based on current language
  const getComplexityOptions = () => i18n.language === 'ar' ? complexityOptionsArabic : complexityOptions;
  
  // Function to translate complexity from English to Arabic
  const translateComplexity = (complexity) => {
    if (i18n.language !== 'ar') return complexity;
    const complexityMap = {
      'Easy': 'سهل',
      'Moderate': 'متوسط', 
      'Complex': 'معقد',
      'All': 'الكل'
    };
    return complexityMap[complexity] || complexity;
  };
  const [ideaDescription, setIdeaDescription] = useState('');
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [filteredIdeas, setFilteredIdeas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [showIdeasVault, setShowIdeasVault] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [chatResponse, setChatResponse] = useState('');
  const [generatedContent, setGeneratedContent] = useState({
    landingPage: null,
    marketingStrategy: null,
    logo: null,
    marketAnalysis: null,
    technicalGuidance: null
  });
  const [isGenerating, setIsGenerating] = useState({
    landingPage: false,
    marketingStrategy: false,
    logo: false,
    marketAnalysis: false,
    technicalGuidance: false
  });
  const [activeFeature, setActiveFeature] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [, startTransition] = useTransition();
  const textareaRef = useRef(null);
  const [selectedLogoConcept, setSelectedLogoConcept] = useState(null);
  const [generatedLogoImages, setGeneratedLogoImages] = useState([]);
  const [isGeneratingLogoImages, setIsGeneratingLogoImages] = useState(false);

  // Ideas Generator states
  const [isIdeasGeneratorMode, setIsIdeasGeneratorMode] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [industryInput, setIndustryInput] = useState('');
  const [selectedComplexity, setSelectedComplexity] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [generatedIdeas, setGeneratedIdeas] = useState([]);
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
  const [selectedIdeaForDetails, setSelectedIdeaForDetails] = useState(null);

  // Handle credit deduction when needed
  useEffect(() => {
    if (shouldDeductCredit && user && user.tier === 'free') {
      setUser(prevUser => ({
        ...prevUser,
        credits: Math.max(0, (prevUser.credits || 0) - 1)
      }));
      setShouldDeductCredit(false);
    }
  }, [shouldDeductCredit, user, setUser]);

  // Track mouse movement for dynamic effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  }, []);

  // Populate idea data from URL parameters
  useEffect(() => {
    const ideaTitle = searchParams.get('title');
    const ideaDesc = searchParams.get('description');
    
    if (ideaTitle && ideaDesc) {
      const combinedText = `${ideaTitle}\n\n${ideaDesc}`;
      setIdeaDescription(combinedText);
      
      // Adjust textarea height after setting content
      setTimeout(() => {
        adjustTextareaHeight();
      }, 100);
    }
  }, [searchParams, adjustTextareaHeight]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [ideaDescription, adjustTextareaHeight]);

  // Fetch ideas for suggestions
  useEffect(() => {
    const fetchIdeas = async () => {
      try {
        setLoadingIdeas(true);
        const currentLanguage = i18n.language || 'en';
        const response = await apiClient.getIdeas({}, 1, 200, currentLanguage); // Get all ideas for comprehensive search functionality
        const ideas = response.ideas || response.data || response || [];
        
        // Transform Supabase response format for each idea
        const transformedIdeas = ideas.map(idea => {
          if (idea) {
            // Transform competitors from array or null to array
            idea.competitors = idea.idea_competitors || idea.competitors || [];
            
            // Transform financial data from array to object
            idea.financialData = idea.idea_financial_data?.[0] || idea.financialData || null;
            
            // Transform technical requirements from array to object  
            idea.technicalRequirements = idea.idea_technical_requirements?.[0] || idea.technicalRequirements || null;
          }
          return idea;
        });
        
        setIdeas(transformedIdeas);
        setFilteredIdeas(transformedIdeas);
      } catch (err) {
        console.error('Failed to fetch ideas:', err);
        setIdeas([]);
        setFilteredIdeas([]);
      } finally {
        setLoadingIdeas(false);
      }
    };

    fetchIdeas();
  }, [i18n.language]);

  // Search ideas directly from Supabase for comprehensive search
  const searchIdeasFromSupabase = async (query) => {
    try {
      setLoadingIdeas(true);
      const currentLanguage = i18n.language || 'en';

      // Query Supabase directly for search across ALL ideas
      const { data: searchResults, error } = await supabase
        .from('ideas')
        .select(`
          *,
          idea_competitors(*),
          idea_financial_data(*),
          idea_technical_requirements(*)
        `)
        .or(`title.ilike.%${query}%,description.ilike.%${query}%,industry.ilike.%${query}%,type.ilike.%${query}%,title_ar.ilike.%${query}%,description_ar.ilike.%${query}%`)
        .order('created_at', { ascending: false })
        .limit(100); // Limit search results to 100 for performance

      if (error) throw error;

      // Transform search results same as main ideas
      const transformedResults = searchResults.map(idea => {
        const transformedIdea = {
          ...idea,
          // Use language-specific content
          title: currentLanguage === 'ar' && idea.title_ar ? idea.title_ar : idea.title,
          description: currentLanguage === 'ar' && idea.description_ar ? idea.description_ar : idea.description,
          // Keep original English values
          title_en: idea.title,
          description_en: idea.description,
        };

        // Transform related data
        transformedIdea.competitors = idea.idea_competitors || [];
        transformedIdea.financialData = idea.idea_financial_data?.[0] || null;
        transformedIdea.technicalRequirements = idea.idea_technical_requirements?.[0] || null;

        return transformedIdea;
      });

      setFilteredIdeas(transformedResults);
    } catch (err) {
      console.error('Failed to search ideas:', err);
      setFilteredIdeas([]);
    } finally {
      setLoadingIdeas(false);
    }
  };

  // Filter ideas based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      // Show only first 8 ideas when no search query to keep UI clean
      setFilteredIdeas(ideas.slice(0, 8));
    } else {
      // Search all ideas in Supabase when user types
      searchIdeasFromSupabase(searchQuery.trim());
    }
  }, [searchQuery, ideas]);

  const handleIdeaSelect = (idea) => {
    setSelectedIdea(idea);
    
    // Only paste title and description for cleaner chat input
    const simpleDescription = `${idea.title}\n\n${idea.description}`;
    
    setIdeaDescription(simpleDescription);
    setShowIdeasVault(false); // Close the popup
    adjustTextareaHeight();
  };

  const getComprehensiveDescription = () => {
    if (selectedIdea) {
      // Create comprehensive idea description for AI processing when using structured AI features
      return [
        `Title: ${selectedIdea.title}`,
        `Industry: ${selectedIdea.industry}`,
        `Type: ${selectedIdea.type}`,
        `Description: ${selectedIdea.description}`,
        selectedIdea.why_now ? `Why Now: ${selectedIdea.why_now}` : '',
        selectedIdea.target_audience ? `Target Audience: ${selectedIdea.target_audience}` : '',
        selectedIdea.revenue_model ? `Revenue Model: ${selectedIdea.revenue_model}` : '',
        selectedIdea.go_to_market ? `Go-to-Market Strategy: ${selectedIdea.go_to_market}` : '',
        // Add financial data if available
        selectedIdea.financialData?.estimated_revenue ? `Estimated Revenue: ${selectedIdea.financialData.estimated_revenue}` : '',
        selectedIdea.financialData?.pricing_model ? `Pricing Model: ${selectedIdea.financialData.pricing_model}` : '',
        // Add technical requirements if available
        selectedIdea.technicalRequirements?.required_skills ? `Required Skills: ${selectedIdea.technicalRequirements.required_skills}` : '',
        selectedIdea.technicalRequirements?.development_time ? `Development Time: ${selectedIdea.technicalRequirements.development_time}` : '',
        // Add competitor info if available
        selectedIdea.competitors?.length > 0 ? `Competitors: ${selectedIdea.competitors.map(c => c.name).join(', ')}` : ''
      ].filter(Boolean).join('\n\n');
    }
    return ideaDescription;
  };

  const generateContent = async (type) => {
    setIsGenerating(prev => ({ ...prev, [type]: true }));
    setActiveFeature(type);
    
    try {
      // API URL is handled by apiClient
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Please login to use AI features');
      }
      
      // Check if user has credits for trial users
      if (user && user.tier === 'free' && (user.credits || 0) <= 0) {
        throw new Error('You have run out of credits. Please subscribe to continue using AI features.');
      }
      
      const currentDescription = getComprehensiveDescription();
      if (!currentDescription.trim()) {
        throw new Error('Please describe your business idea first');
      }

      // Add language instruction for Arabic mode
      let enhancedDescription = currentDescription;
      if (i18n.language === 'ar') {
        enhancedDescription = `${currentDescription}\n\nIMPORTANT: Please generate your response in Arabic language. Keep technical terms in English when necessary for clarity. Ensure the content is culturally appropriate for Arabic-speaking entrepreneurs.`;
      }

      const data = await apiClient.generateContent(type, enhancedDescription, token);

      // Record AI interaction for analytics
      if (selectedIdea?.id) {
        try {
          await recordAIInteraction(selectedIdea.id, type);
        } catch (error) {
          console.error('Failed to record AI interaction:', error);
        }
      }

      setGeneratedContent(prev => ({
        ...prev,
        [type]: data.content
      }));
      
    } catch (error) {
      console.error('Error generating content:', error);
      alert(error.message || 'Failed to generate content. Please try again.');
      return; // Exit early on error, don't deduct credit
    } finally {
      setIsGenerating(prev => ({ ...prev, [type]: false }));
    }
    
    // Trigger credit deduction for trial users after successful generation
    if (user && user.tier === 'free') {
      setShouldDeductCredit(true);
    }
  };

  const sendChatMessage = async () => {
    if (!ideaDescription.trim() || isSendingMessage) return;
    
    setIsSendingMessage(true);
    setChatResponse('');
    
    try {
      // API URL is handled by apiClient
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Please login to use chat features');
      }
      
      // Check if user has credits for trial users
      if (user && user.tier === 'free' && (user.credits || 0) <= 0) {
        throw new Error('You have run out of credits. Please subscribe to continue using AI features.');
      }

      const response = await apiClient.aiChatStream(ideaDescription, [], i18n.language, token);

      if (!response.ok) {
        throw new Error('Failed to get response from Gemini');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      while (true) {
        const { done, value: chunk } = await reader.read();
        if (done) break;

        const text = decoder.decode(chunk);
        fullResponse += text;
        setChatResponse(fullResponse);
      }
      
    } catch (error) {
      console.error('Error calling Gemini API:', error);
      setChatResponse('Sorry, I encountered an error. Please try again.');
      return; // Exit early on error, don't deduct credit
    } finally {
      setIsSendingMessage(false);
    }
    
    // Trigger credit deduction for trial users after successful chat response
    if (user && user.tier === 'free') {
      setShouldDeductCredit(true);
    }
  };

  const generateLogoImages = async () => {
    if (!selectedLogoConcept) {
      alert(i18n.language === 'ar' ? 'يرجى اختيار مفهوم الشعار أولاً' : 'Please select a logo concept first');
      return;
    }

    setIsGeneratingLogoImages(true);
    
    try {
      // API URL is handled by apiClient
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Please login to use AI features');
      }
      
      // Check if user has credits for trial users
      if (user && user.tier === 'free' && (user.credits || 0) <= 0) {
        throw new Error('You have run out of credits. Please subscribe to continue using AI features.');
      }

      const data = await apiClient.generateLogo(selectedLogoConcept, i18n.language, token);
      
      if (data.success && data.logos) {
        setGeneratedLogoImages(data.logos);
      } else {
        throw new Error(data.message || 'No logos were generated');
      }
      
      // Trigger credit deduction for trial users after successful generation
      if (user && user.tier === 'free') {
        setShouldDeductCredit(true);
      }
      
    } catch (error) {
      console.error('Error generating logo images:', error);
      alert(error.message || 'Failed to generate logo images. Please try again.');
    } finally {
      setIsGeneratingLogoImages(false);
    }
  };

  const downloadLogo = (logoData, index) => {
    try {
      // Convert base64 to blob
      const base64Data = logoData.base64.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `logo-${index + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading logo:', error);
      alert('Failed to download logo. Please try again.');
    }
  };

  const generateStartupIdeas = async () => {
    if (!selectedCountry || !industryInput.trim() || !selectedComplexity) {
      alert(i18n.language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill in all required fields');
      return;
    }

    setIsGeneratingIdeas(true);
    
    try {
      // API URL is handled by apiClient
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error(i18n.language === 'ar' ? 'يرجى تسجيل الدخول لاستخدام ميزات الذكاء الاصطناعي' : 'Please login to use AI features');
      }
      
      // Check if user has credits for trial users
      if (user && user.tier === 'free' && (user.credits || 0) <= 0) {
        throw new Error(i18n.language === 'ar' ? 'لقد نفدت نقاط رصيدك. يرجى الاشتراك للمتابعة.' : 'You have run out of credits. Please subscribe to continue using AI features.');
      }

      const data = await apiClient.generateStartupIdeas({
        country: selectedCountry,
        industry: industryInput,
        complexity: selectedComplexity,
        additionalNotes: additionalNotes,
        language: i18n.language
      }, token);
      
      // Handle different response formats
      let ideas = [];
      if (data.ideas && Array.isArray(data.ideas)) {
        ideas = data.ideas;
      } else if (Array.isArray(data)) {
        ideas = data;
      } else if (data && typeof data === 'object') {
        // If it's a single idea object, wrap it in an array
        ideas = [data];
      }
      
      setGeneratedIdeas(ideas);
      
      // Trigger credit deduction for trial users after successful generation
      if (user && user.tier === 'free') {
        setShouldDeductCredit(true);
      }
      
    } catch (error) {
      console.error('Error generating startup ideas:', error);
      alert(error.message || (i18n.language === 'ar' ? 'فشل في توليد أفكار المشاريع. يرجى المحاولة مرة أخرى.' : 'Failed to generate startup ideas. Please try again.'));
    } finally {
      setIsGeneratingIdeas(false);
    }
  };

  const aiFeatures = [
    { 
      type: 'landingPage',
      icon: <Code className="h-5 w-5" />, 
      label: t('buildersStudio.landingPage'), 
      action: () => generateContent('landingPage') 
    },
    { 
      type: 'logo',
      icon: <Palette className="h-5 w-5" />, 
      label: t('buildersStudio.logo'), 
      action: () => generateContent('logo') 
    },
    { 
      type: 'marketingStrategy',
      icon: <TrendingUp className="h-5 w-5" />, 
      label: t('buildersStudio.marketingStrategy'), 
      action: () => generateContent('marketingStrategy') 
    },
    { 
      type: 'marketAnalysis',
      icon: <BarChart3 className="h-5 w-5" />, 
      label: t('buildersStudio.marketAnalysis'), 
      action: () => generateContent('marketAnalysis') 
    },
    { 
      type: 'technicalGuidance',
      icon: <BookOpen className="h-5 w-5" />, 
      label: t('buildersStudio.technicalGuidance'), 
      action: () => generateContent('technicalGuidance') 
    }
  ];

  return (
    <div className="min-h-screen overflow-hidden bg-transparent">
      {/* Animated background elements */}
      <div className="absolute inset-0 h-full w-full overflow-hidden">
        <div className="absolute left-1/4 top-0 h-96 w-96 animate-pulse rounded-full bg-blue-500/10 mix-blend-normal blur-[128px] filter" />
        <div className="absolute bottom-0 right-1/4 h-96 w-96 animate-pulse rounded-full bg-purple-500/10 mix-blend-normal blur-[128px] filter delay-700" />
        <div className="absolute right-1/3 top-1/4 h-64 w-64 animate-pulse rounded-full bg-purple-500/10 mix-blend-normal blur-[96px] filter delay-1000" />
      </div>

      {/* Mouse follower effect */}
      <motion.div
        className="pointer-events-none fixed z-0 h-[50rem] w-[50rem] rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-purple-500 opacity-[0.02] blur-[96px]"
        animate={{
          x: mousePosition.x - 400,
          y: mousePosition.y - 400,
        }}
        transition={{
          type: 'spring',
          damping: 25,
          stiffness: 150,
          mass: 0.5,
        }}
      />

      <div className="relative z-10 mx-auto max-w-6xl px-6 py-8">
        {/* Hero Section */}
        <motion.div
          className="mb-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="flex items-center justify-between">
                <h1 className={cn(
                  "text-4xl md:text-5xl font-bold pb-2",
                  darkMode ? "text-white" : "text-slate-700",
  "text-start"
                )}>
                  {t('navigation.buildersStudio')}
                </h1>
                
                {/* Credits Display for Trial Users */}
                {user && user.tier === 'free' && (
                  <motion.div
                    className={cn(
                      "px-4 py-2 rounded-lg border",
                      (user.credits || 0) > 0 
                        ? "bg-blue-50 border-blue-200 text-blue-700"
                        : "bg-red-50 border-red-200 text-red-700",
                      darkMode && (user.credits || 0) > 0 
                        ? "bg-blue-900/20 border-blue-800 text-blue-300"
                        : darkMode && "bg-red-900/20 border-red-800 text-red-300"
                    )}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                      </svg>
                      <span className="text-sm font-medium">
                        {user.credits || 0} {i18n.language === 'ar' ? 'نقاط رصيد' : 'credits left'}
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
              <motion.div
                className="h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent mx-auto"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: '60%', opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              />
            </motion.div>
            <motion.p
              className={cn(
                "text-xl",
                darkMode ? "text-gray-300" : "text-slate-500",
"text-start"
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {t('buildersStudio.transformIdea')}
            </motion.p>
          </div>
        </motion.div>

        {/* Out of Credits Banner */}
        {user && user.tier === 'free' && (user.credits || 0) <= 0 && (
          <motion.div
            className="mb-8 p-4 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold mb-1">
                  {i18n.language === 'ar' ? 'لا يوجد نقاط رصيد كافية' : 'Out of Credits!'}
                </h3>
                <p className="text-sm opacity-90">
                  {i18n.language === 'ar' 
                    ? 'لقد استنفدت جميع نقاط رصيدك. يرجى الاشتراك للاستمرار في استخدام ميزات الذكاء الاصطناعي.'
                    : 'You have used all your credits. Please subscribe to continue using AI features.'
                  }
                </p>
              </div>
              <motion.button
                className="px-4 py-2 bg-white text-red-600 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  // Navigate to profile billing tab
                  navigate('/profile#billing');
                }}
              >
                {i18n.language === 'ar' ? 'ترقية الآن' : 'Upgrade Now'}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Ideas Generator Mode Selector */}
        <motion.div
          className="mb-6 flex justify-center"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className={cn(
            "flex items-center gap-2 p-2 rounded-xl border",
            darkMode ? "border-gray-700 bg-gray-800/80" : "border-gray-200 bg-white/80",
            i18n.language === 'ar' && "flex-row-reverse"
          )}>
            {/* Builder's Studio Button */}
            <motion.button
              onClick={() => setIsIdeasGeneratorMode(false)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
                !isIdeasGeneratorMode
                  ? "bg-blue-500 text-white shadow-md"
                  : "text-gray-500 hover:text-blue-600",
                darkMode && !isIdeasGeneratorMode && "bg-blue-600",
                darkMode && isIdeasGeneratorMode && "text-gray-400 hover:text-blue-400",
                i18n.language === 'ar' && "flex-row-reverse"
              )}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Building className="h-4 w-4" />
              {i18n.language === 'ar' ? 'استوديو البناة' : 'Builder\'s Studio'}
            </motion.button>

            {/* Ideas Generator Button */}
            <motion.button
              onClick={() => setIsIdeasGeneratorMode(true)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
                isIdeasGeneratorMode
                  ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-md"
                  : "text-gray-500 hover:text-purple-600",
                darkMode && !isIdeasGeneratorMode && "text-gray-400 hover:text-purple-400",
                i18n.language === 'ar' && "flex-row-reverse"
              )}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Lightbulb className="h-4 w-4" />
              {i18n.language === 'ar' ? 'مولد الأفكار' : 'Ideas Generator'}
            </motion.button>
          </div>
        </motion.div>

        {/* Ideas Generator Form - Only show when in Ideas Generator mode */}
        {isIdeasGeneratorMode && (
          <motion.div
            className={cn(
              "mb-8 rounded-2xl border shadow-2xl backdrop-blur-2xl",
              darkMode ? "border-gray-700 bg-gray-800/80" : "border-gray-200 bg-white/80"
            )}
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <div className={cn(
              "p-6 md:p-8",
              i18n.language === 'ar' && "rtl"
            )}>
              <motion.h3
                className={cn(
                  "text-2xl font-bold mb-6 text-center",
                  darkMode ? "text-white" : "text-slate-700"
                )}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Lightbulb className={cn(
                  "inline-block h-6 w-6 mb-1",
                  i18n.language === 'ar' ? "ml-2" : "mr-2"
                )} />
                {i18n.language === 'ar' ? 'مولد أفكار المشاريع الناشئة' : 'Startup Ideas Generator'}
              </motion.h3>

              <div className={cn(
                "grid grid-cols-1 md:grid-cols-2 gap-6",
                i18n.language === 'ar' && "text-right"
              )}>
                {/* Country Dropdown */}
                <div>
                  <label className={cn(
                    "block text-sm font-medium mb-2",
                    darkMode ? "text-gray-300" : "text-gray-700"
                  )}>
                    {i18n.language === 'ar' ? (
                      <span className="flex items-center justify-end">
                        <Globe className="h-4 w-4 ml-2" />
                        الدولة
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Globe className="h-4 w-4 mr-2" />
                        Country
                      </span>
                    )}
                  </label>
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className={cn(
                      "w-full px-4 py-3 rounded-xl border transition-all",
                      "focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500",
                      darkMode 
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    )}
                    style={i18n.language === 'ar' ? { 
                      direction: 'rtl', 
                      textAlign: 'right',
                      unicodeBidi: 'bidi-override'
                    } : {}}
                  >
                    <option value="">
                      {i18n.language === 'ar' ? 'اختر الدولة' : 'Select Country'}
                    </option>
                    {getCountriesList().map((country, index) => (
                      <option key={countries[index]} value={countries[index]}>{country}</option>
                    ))}
                  </select>
                </div>

                {/* Industry Input */}
                <div>
                  <label className={cn(
                    "block text-sm font-medium mb-2",
                    darkMode ? "text-gray-300" : "text-gray-700"
                  )}>
                    {i18n.language === 'ar' ? (
                      <span className="flex items-center justify-end">
                        <Building className="h-4 w-4 ml-2" />
                        الصناعة
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Building className="h-4 w-4 mr-2" />
                        Industry
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={industryInput}
                    onChange={(e) => setIndustryInput(e.target.value)}
                    placeholder={i18n.language === 'ar' ? 'مثال: التكنولوجيا، الصحة، التعليم' : 'e.g., Technology, Healthcare, Education'}
                    className={cn(
                      "w-full px-4 py-3 rounded-xl border transition-all",
                      "focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500",
                      darkMode 
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    )}
                    style={i18n.language === 'ar' ? { 
                      direction: 'rtl', 
                      textAlign: 'right',
                      unicodeBidi: 'bidi-override'
                    } : {}}
                  />
                </div>

                {/* Complexity Dropdown */}
                <div>
                  <label className={cn(
                    "block text-sm font-medium mb-2",
                    darkMode ? "text-gray-300" : "text-gray-700"
                  )}>
                    {i18n.language === 'ar' ? (
                      <span className="flex items-center justify-end">
                        <BarChart3 className="h-4 w-4 ml-2" />
                        التعقيد
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Complexity
                      </span>
                    )}
                  </label>
                  <select
                    value={selectedComplexity}
                    onChange={(e) => setSelectedComplexity(e.target.value)}
                    className={cn(
                      "w-full px-4 py-3 rounded-xl border transition-all",
                      "focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500",
                      darkMode 
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    )}
                    style={i18n.language === 'ar' ? { 
                      direction: 'rtl', 
                      textAlign: 'right',
                      unicodeBidi: 'bidi-override'
                    } : {}}
                  >
                    <option value="">
                      {i18n.language === 'ar' ? 'اختر التعقيد' : 'Select Complexity'}
                    </option>
                    {getComplexityOptions().map((complexity, index) => (
                      <option key={complexityOptions[index]} value={complexityOptions[index]}>{complexity}</option>
                    ))}
                  </select>
                </div>

                {/* Additional Notes */}
                <div>
                  <label className={cn(
                    "block text-sm font-medium mb-2",
                    darkMode ? "text-gray-300" : "text-gray-700"
                  )}>
                    {i18n.language === 'ar' ? (
                      <span className="flex items-center justify-end">
                        <BookOpen className="h-4 w-4 ml-2" />
                        ملاحظات إضافية
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <BookOpen className="h-4 w-4 mr-2" />
                        Additional Notes
                      </span>
                    )}
                  </label>
                  <textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    placeholder={i18n.language === 'ar' ? 'أي متطلبات أو تفضيلات إضافية...' : 'Any additional requirements or preferences...'}
                    rows={3}
                    className={cn(
                      "w-full px-4 py-3 rounded-xl border transition-all resize-none",
                      "focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500",
                      darkMode 
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    )}
                    style={i18n.language === 'ar' ? { 
                      direction: 'rtl', 
                      textAlign: 'right',
                      unicodeBidi: 'bidi-override'
                    } : {}}
                  />
                </div>
              </div>

              {/* Generate Ideas Button */}
              <div className="flex justify-center mt-8">
                <motion.button
                  onClick={generateStartupIdeas}
                  disabled={isGeneratingIdeas || !selectedCountry || !industryInput.trim() || !selectedComplexity || (user && user.tier === 'free' && (user.credits || 0) <= 0)}
                  className={cn(
                    "flex items-center gap-3 px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg",
                    isGeneratingIdeas || !selectedCountry || !industryInput.trim() || !selectedComplexity || (user && user.tier === 'free' && (user.credits || 0) <= 0)
                      ? "cursor-not-allowed opacity-50 bg-gray-400"
                      : "bg-gradient-to-r from-purple-500 via-blue-500 to-purple-600 text-white hover:shadow-xl hover:scale-105"
                  )}
                  whileHover={!isGeneratingIdeas && selectedCountry && industryInput.trim() && selectedComplexity && !(user && user.tier === 'free' && (user.credits || 0) <= 0) ? { scale: 1.05 } : {}}
                  whileTap={!isGeneratingIdeas && selectedCountry && industryInput.trim() && selectedComplexity && !(user && user.tier === 'free' && (user.credits || 0) <= 0) ? { scale: 0.95 } : {}}
                >
                  {isGeneratingIdeas ? (
                    <>
                      <Loader className="h-6 w-6 animate-spin" />
                      {i18n.language === 'ar' ? 'جاري التوليد...' : 'Generating...'}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-6 w-6" />
                      {i18n.language === 'ar' ? 'توليد الأفكار' : 'Generate Ideas'}
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Original Idea Input Box - Only show when NOT in Ideas Generator mode */}
        {!isIdeasGeneratorMode && (
          <motion.div
            className={cn(
              "mb-8 rounded-2xl border shadow-2xl backdrop-blur-2xl",
              darkMode ? "border-gray-700 bg-gray-800/80" : "border-gray-200 bg-white/80"
            )}
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <div className="p-6 md:p-8">
            {/* Header with Select from Vault button */}
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                {/* Removed label text */}
              </div>
              <motion.button
                onClick={() => setShowIdeasVault(true)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all text-sm",
                  darkMode 
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                )}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Building className="h-4 w-4" />
                {i18n.language === 'ar' ? 'اختر فكرة من المخزن' : 'Select an Idea from vault'}
              </motion.button>
            </div>
            
            {/* Textarea with Send Button */}
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={ideaDescription}
                onChange={(e) => setIdeaDescription(e.target.value)}
                placeholder={t('buildersStudio.ideaPlaceholder')}
                className={cn(
                  "w-full min-h-[120px] px-4 py-3 rounded-xl border transition-all resize-none",
                  i18n.language === 'ar' ? "pl-32 pr-4" : "pr-32 pl-4",
                  "focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500",
                  darkMode 
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500",
                  "text-start"
                )}
                onInput={adjustTextareaHeight}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
              />
              
              {/* Ask AI Agent Button */}
              <motion.button
                onClick={sendChatMessage}
                disabled={!ideaDescription.trim() || isSendingMessage || (user && user.tier === 'free' && (user.credits || 0) <= 0)}
                className={cn(
                  "absolute bottom-3 flex items-center gap-2 px-3 py-2 rounded-lg transition-all",
                  i18n.language === 'ar' ? "left-3" : "right-3",
                  ideaDescription.trim() && !isSendingMessage && !(user && user.tier === 'free' && (user.credits || 0) <= 0)
                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:shadow-lg"
                    : darkMode
                      ? "bg-gray-600 text-gray-400"
                      : "bg-gray-200 text-gray-400"
                )}
                whileHover={ideaDescription.trim() && !isSendingMessage && !(user && user.tier === 'free' && (user.credits || 0) <= 0) ? { scale: 1.05 } : {}}
                whileTap={ideaDescription.trim() && !isSendingMessage && !(user && user.tier === 'free' && (user.credits || 0) <= 0) ? { scale: 0.95 } : {}}
              >
                {isSendingMessage ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span className="text-sm font-medium">
                      {i18n.language === 'ar' ? 'جاري المعالجة...' : 'Processing...'}
                    </span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {i18n.language === 'ar' ? 'اسأل الذكاء الاصطناعي' : 'Ask AI Agent'}
                    </span>
                  </>
                )}
              </motion.button>

              {/* Character Count */}
              <motion.div
                className={cn(
                  "absolute bottom-2 flex items-center gap-2",
                  i18n.language === 'ar' ? "right-2" : "left-2"
                )}
                initial={{ opacity: 0 }}
                animate={{ opacity: ideaDescription ? 1 : 0 }}
              >
                <span className={cn(
                  "text-xs",
                  darkMode ? "text-gray-400" : "text-gray-500"
                )}>
                  {ideaDescription.length} {t('buildersStudio.characters')}
                </span>
              </motion.div>
            </div>

            {/* Chat Response Display */}
            <AnimatePresence>
              {chatResponse && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4"
                >
                  <div className={cn(
                    "p-4 rounded-xl border",
                    darkMode 
                      ? "bg-gray-700/50 border-gray-600 text-gray-200"
                      : "bg-gray-50 border-gray-200 text-gray-800"
                  )}>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-4 w-4 text-blue-500" />
                      <span className={cn(
                        "text-sm font-medium",
                        darkMode ? "text-gray-300" : "text-gray-600"
                      )}>
                        Agent Response
                      </span>
                    </div>
                    <div className={cn(
                      "text-sm leading-relaxed prose prose-sm max-w-none",
                      darkMode ? "prose-invert" : ""
                    )}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({node, ...props}) => <h1 className={cn("text-lg font-bold mb-3 mt-4", darkMode ? "text-gray-200" : "text-gray-800")} {...props} />,
                          h2: ({node, ...props}) => <h2 className={cn("text-base font-bold mb-2 mt-3", darkMode ? "text-gray-200" : "text-gray-800")} {...props} />,
                          h3: ({node, ...props}) => <h3 className={cn("text-sm font-bold mb-2 mt-3", darkMode ? "text-gray-200" : "text-gray-800")} {...props} />,
                          p: ({node, ...props}) => <p className={cn("mb-3", darkMode ? "text-gray-300" : "text-gray-700")} {...props} />,
                          strong: ({node, ...props}) => <strong className={cn("font-semibold", darkMode ? "text-gray-100" : "text-gray-900")} {...props} />,
                          code: ({node, inline, ...props}) => 
                            inline 
                              ? <code className={cn("px-1 py-0.5 rounded text-xs font-mono", darkMode ? "bg-gray-800 text-blue-300" : "bg-gray-100 text-blue-600")} {...props} />
                              : <code className={cn("block p-3 rounded-lg text-xs font-mono", darkMode ? "bg-gray-800 text-gray-200" : "bg-gray-100 text-gray-800")} {...props} />,
                          ul: ({node, ...props}) => <ul className={cn("mb-3 ml-4 list-disc", darkMode ? "text-gray-300" : "text-gray-700")} {...props} />,
                          ol: ({node, ...props}) => <ol className={cn("mb-3 ml-4 list-decimal", darkMode ? "text-gray-300" : "text-gray-700")} {...props} />,
                          li: ({node, ...props}) => <li className="mb-1" {...props} />,
                          blockquote: ({node, ...props}) => <blockquote className={cn("border-l-4 pl-4 italic my-3", darkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600")} {...props} />,
                          table: ({node, ...props}) => <table className={cn("w-full mb-4 border-collapse rounded-lg overflow-hidden shadow-sm", darkMode ? "bg-gray-800" : "bg-white")} {...props} />,
                          thead: ({node, ...props}) => <thead className={cn("", darkMode ? "bg-gray-700" : "bg-gray-50")} {...props} />,
                          tbody: ({node, ...props}) => <tbody {...props} />,
                          tr: ({node, ...props}) => <tr className={cn("border-b", darkMode ? "border-gray-600 hover:bg-gray-700/50" : "border-gray-200 hover:bg-gray-50")} {...props} />,
                          th: ({node, ...props}) => <th className={cn("px-4 py-3 text-left font-semibold text-sm", darkMode ? "text-gray-200 border-gray-600" : "text-gray-900 border-gray-300")} {...props} />,
                          td: ({node, ...props}) => <td className={cn("px-4 py-3 text-sm", darkMode ? "text-gray-300 border-gray-600" : "text-gray-700 border-gray-300")} {...props} />,
                        }}
                      >
                        {chatResponse}
                      </ReactMarkdown>
                      {isSendingMessage && (
                        <motion.span
                          animate={{ opacity: [1, 0] }}
                          transition={{ duration: 0.8, repeat: Infinity }}
                          className="ml-1"
                        >
                          ▋
                        </motion.span>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          </motion.div>
        )}
        
        {/* Generated Ideas Display - Only show in Ideas Generator mode */}
        {isIdeasGeneratorMode && generatedIdeas.length > 0 && (
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className={cn(
              "text-2xl font-bold mb-6 text-center",
              darkMode ? "text-white" : "text-slate-700"
            )}>
              {i18n.language === 'ar' ? 'الأفكار المُولدة' : 'Generated Ideas'}
            </h3>
            
            <div className={cn(
              "grid grid-cols-1 md:grid-cols-2 gap-6 items-start",
              i18n.language === 'ar' && "text-right"
            )}>
              {generatedIdeas.map((idea, index) => (
                <motion.div
                  key={index}
                  className={cn(
                    "p-6 rounded-xl border cursor-pointer transition-all",
                    darkMode 
                      ? "border-gray-600 bg-gray-700/50 hover:border-purple-500"
                      : "border-gray-200 bg-white hover:border-purple-500",
                    "hover:shadow-lg"
                  )}
                  style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}
                  onClick={() => setSelectedIdeaForDetails(idea)}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <span className={cn(
                      "px-3 py-1 rounded-full text-xs font-medium",
                      "bg-gradient-to-r from-purple-500 to-blue-500 text-white"
                    )}>
                      {translateComplexity(idea.complexity)}
                    </span>
                    <span className={cn(
                      "text-sm",
                      darkMode ? "text-gray-400" : "text-gray-500"
                    )}>
                      {idea.industry}
                    </span>
                  </div>
                  
                  <h4 className={cn(
                    "text-xl font-bold mb-3",
                    darkMode ? "text-white" : "text-slate-700"
                  )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                    {idea.name}
                  </h4>
                  
                  <p className={cn(
                    "text-sm mb-4 leading-relaxed",
                    darkMode ? "text-gray-300" : "text-slate-600"
                  )} style={{
                    ...(i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}),
                    overflow: 'visible',
                    display: 'block',
                    webkitLineClamp: 'unset',
                    webkitBoxOrient: 'unset',
                    textOverflow: 'unset',
                    whiteSpace: 'normal'
                  }}>
                    {idea.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    {i18n.language === 'ar' ? (
                      <>
                        <motion.button
                          className="text-purple-500 hover:text-purple-600 text-sm font-medium"
                          whileHover={{ scale: 1.05 }}
                          onClick={() => setSelectedIdeaForDetails(idea)}
                        >
                          عرض التفاصيل ←
                        </motion.button>
                        <div className="flex items-center gap-2 flex-row-reverse">
                          <TrendingUp className="h-4 w-4 text-green-500" />
                          <span className={cn(
                            "text-xs",
                            darkMode ? "text-gray-400" : "text-gray-500"
                          )} style={{ textAlign: 'right', direction: 'rtl' }}>
                            {idea.financialProjections?.arr}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-green-500" />
                          <span className={cn(
                            "text-xs",
                            darkMode ? "text-gray-400" : "text-gray-500"
                          )}>
                            {idea.financialProjections?.arr}
                          </span>
                        </div>
                        <motion.button
                          className="text-purple-500 hover:text-purple-600 text-sm font-medium"
                          whileHover={{ scale: 1.05 }}
                          onClick={() => setSelectedIdeaForDetails(idea)}
                        >
                          View Details →
                        </motion.button>
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Detailed Idea View Modal */}
        <AnimatePresence>
          {selectedIdeaForDetails && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Backdrop */}
              <motion.div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedIdeaForDetails(null)}
              />

              {/* Modal */}
              <motion.div
                className={cn(
                  "relative z-10 w-full max-w-6xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden",
                  darkMode ? "bg-gray-900" : "bg-white"
                )}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
              >
                {/* Header */}
                <div className={cn(
                  "flex items-center justify-between p-6 border-b",
                  darkMode ? "border-gray-700" : "border-gray-200",
                  i18n.language === 'ar' && "flex-row-reverse"
                )}>
                  <div>
                    <h3 className={cn(
                      "text-2xl font-bold",
                      darkMode ? "text-white" : "text-gray-900"
                    )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                      {selectedIdeaForDetails.name}
                    </h3>
                    <div className={cn(
                      "flex items-center gap-3 mt-2",
                      i18n.language === 'ar' && "flex-row-reverse justify-end"
                    )} style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>
                      <span className={cn(
                        "px-3 py-1 rounded-full text-xs font-medium",
                        "bg-gradient-to-r from-purple-500 to-blue-500 text-white"
                      )} style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>
                        {translateComplexity(selectedIdeaForDetails.complexity)}
                      </span>
                      <span className={cn(
                        "text-sm",
                        darkMode ? "text-gray-400" : "text-gray-500"
                      )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                        {selectedIdeaForDetails.industry}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedIdeaForDetails(null)}
                    className={cn(
                      "p-2 rounded-lg transition-colors",
                      darkMode
                        ? "text-gray-400 hover:text-white hover:bg-gray-700"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    )}
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className={cn("p-6 max-h-[calc(90vh-120px)] overflow-y-auto", i18n.language === 'ar' && "text-right")}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {/* Financial Projections */}
                    <motion.div
                      className={cn(
                        "p-6 rounded-xl",
                        "bg-gradient-to-br from-green-500/10 to-emerald-500/10",
                        darkMode ? "border border-green-500/20" : "border border-green-500/30"
                      )}
                      style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <h4 className={cn(
                        "text-lg font-bold mb-4",
                        darkMode ? "text-green-400" : "text-green-600",
                        i18n.language === 'ar' ? "block text-right" : "flex items-center gap-2"
                      )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                        {i18n.language === 'ar' ? (
                          <>
                            {i18n.language === 'ar' ? 'الإسقاطات المالية' : 'Financial Projections'}
                            <TrendingUp className="h-5 w-5 inline mr-2" />
                          </>
                        ) : (
                          <>
                            <TrendingUp className="h-5 w-5" />
                            {i18n.language === 'ar' ? 'الإسقاطات المالية' : 'Financial Projections'}
                          </>
                        )}
                      </h4>
                                              <div className="space-y-3" style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>
                        <div style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                          <span className="text-sm font-medium opacity-75" style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>ARR:</span>
                          <p className={cn("text-lg font-bold", darkMode ? "text-white" : "text-gray-900")} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                            {selectedIdeaForDetails.financialProjections?.arr}
                          </p>
                        </div>
                        <div style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                          <span className="text-sm font-medium opacity-75" style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>TAM:</span>
                          <p className={cn("text-lg font-bold", darkMode ? "text-white" : "text-gray-900")} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                            {selectedIdeaForDetails.financialProjections?.tam}
                          </p>
                        </div>
                        <div style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                          <span className="text-sm font-medium opacity-75" style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>SAM:</span>
                          <p className={cn("text-lg font-bold", darkMode ? "text-white" : "text-gray-900")} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                            {selectedIdeaForDetails.financialProjections?.sam}
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Why Now */}
                    <motion.div
                      className={cn(
                        "p-6 rounded-xl",
                        "bg-gradient-to-br from-blue-500/10 to-purple-500/10",
                        darkMode ? "border border-blue-500/20" : "border border-blue-500/30"
                      )}
                      style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <h4 className={cn(
                        "text-lg font-bold mb-4",
                        darkMode ? "text-blue-400" : "text-blue-600",
                        i18n.language === 'ar' ? "block text-right" : "flex items-center gap-2"
                      )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                        {i18n.language === 'ar' ? (
                          <>
                            {i18n.language === 'ar' ? 'لماذا الآن؟' : 'Why Now?'}
                            <Zap className="h-5 w-5 inline mr-2" />
                          </>
                        ) : (
                          <>
                            <Zap className="h-5 w-5" />
                            {i18n.language === 'ar' ? 'لماذا الآن؟' : 'Why Now?'}
                          </>
                        )}
                      </h4>
                      <p className={cn(
                        "text-sm leading-relaxed",
                        darkMode ? "text-gray-300" : "text-gray-700"
                      )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                        {selectedIdeaForDetails.whyNow}
                      </p>
                    </motion.div>

                    {/* Target Audience */}
                    <motion.div
                      className={cn(
                        "p-6 rounded-xl",
                        "bg-gradient-to-br from-purple-500/10 to-pink-500/10",
                        darkMode ? "border border-purple-500/20" : "border border-purple-500/30"
                      )}
                      style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <h4 className={cn(
                        "text-lg font-bold mb-4",
                        darkMode ? "text-purple-400" : "text-purple-600",
                        i18n.language === 'ar' ? "block text-right" : "flex items-center gap-2"
                      )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                        {i18n.language === 'ar' ? (
                          <>
                            {i18n.language === 'ar' ? 'الجمهور المستهدف' : 'Target Audience'}
                            <Globe className="h-5 w-5 inline mr-2" />
                          </>
                        ) : (
                          <>
                            <Globe className="h-5 w-5" />
                            {i18n.language === 'ar' ? 'الجمهور المستهدف' : 'Target Audience'}
                          </>
                        )}
                      </h4>
                      <p className={cn(
                        "text-sm leading-relaxed",
                        darkMode ? "text-gray-300" : "text-gray-700"
                      )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                        {selectedIdeaForDetails.targetAudience}
                      </p>
                    </motion.div>

                    {/* Competitors */}
                    <motion.div
                      className={cn(
                        "p-6 rounded-xl lg:col-span-2",
                        "bg-gradient-to-br from-orange-500/10 to-red-500/10",
                        darkMode ? "border border-orange-500/20" : "border border-orange-500/30"
                      )}
                      style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <h4 className={cn(
                        "text-lg font-bold mb-4",
                        darkMode ? "text-orange-400" : "text-orange-600",
                        i18n.language === 'ar' ? "block text-right" : "flex items-center gap-2"
                      )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                        {i18n.language === 'ar' ? (
                          <>
                            {i18n.language === 'ar' ? 'المنافسون الرئيسيون' : 'Top Competitors'}
                            <BarChart3 className="h-5 w-5 inline mr-2" />
                          </>
                        ) : (
                          <>
                            <BarChart3 className="h-5 w-5" />
                            {i18n.language === 'ar' ? 'المنافسون الرئيسيون' : 'Top Competitors'}
                          </>
                        )}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>
                        {selectedIdeaForDetails.competitors?.map((competitor, index) => (
                          <div key={index} className={cn(
                            "p-4 rounded-lg border",
                            darkMode ? "border-gray-600 bg-gray-800/50" : "border-gray-200 bg-gray-50"
                          )} style={i18n.language === 'ar' ? { direction: 'rtl', textAlign: 'right' } : {}}>
                            <h5 className={cn(
                              "font-bold mb-2",
                              darkMode ? "text-white" : "text-gray-900"
                            )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                              {competitor.name}
                            </h5>
                            <div className="space-y-2" style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>
                              <div style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                                <span className="text-xs font-medium text-green-500" style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>
                                  {i18n.language === 'ar' ? 'نقاط القوة:' : 'Strengths:'}
                                </span>
                                <ul className="text-xs mt-1 space-y-1" style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>
                                  {competitor.strengths?.map((strength, i) => (
                                    <li key={i} className={cn(
                                      "text-gray-600", 
                                      darkMode && "text-gray-400"
                                    )} style={i18n.language === 'ar' ? { direction: 'rtl', textAlign: 'right' } : {}}>
                                      • {strength}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                                <span className="text-xs font-medium text-red-500" style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>
                                  {i18n.language === 'ar' ? 'نقاط الضعف:' : 'Weaknesses:'}
                                </span>
                                <ul className="text-xs mt-1 space-y-1" style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}>
                                  {competitor.weaknesses?.map((weakness, i) => (
                                    <li key={i} className={cn(
                                      "text-gray-600", 
                                      darkMode && "text-gray-400"
                                    )} style={i18n.language === 'ar' ? { direction: 'rtl', textAlign: 'right' } : {}}>
                                      • {weakness}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>

                    {/* Go-to-Market Strategy */}
                    <motion.div
                      className={cn(
                        "p-6 rounded-xl",
                        "bg-gradient-to-br from-teal-500/10 to-cyan-500/10",
                        darkMode ? "border border-teal-500/20" : "border border-teal-500/30"
                      )}
                      style={i18n.language === 'ar' ? { direction: 'rtl' } : {}}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <h4 className={cn(
                        "text-lg font-bold mb-4",
                        darkMode ? "text-teal-400" : "text-teal-600",
                        i18n.language === 'ar' ? "block text-right" : "flex items-center gap-2"
                      )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                        {i18n.language === 'ar' ? (
                          <>
                            {i18n.language === 'ar' ? 'استراتيجية دخول السوق' : 'Go-to-Market Strategy'}
                            <ChevronRight className="h-5 w-5 inline mr-2" />
                          </>
                        ) : (
                          <>
                            <ChevronRight className="h-5 w-5" />
                            {i18n.language === 'ar' ? 'استراتيجية دخول السوق' : 'Go-to-Market Strategy'}
                          </>
                        )}
                      </h4>
                      <p className={cn(
                        "text-sm leading-relaxed",
                        darkMode ? "text-gray-300" : "text-gray-700"
                      )} style={i18n.language === 'ar' ? { textAlign: 'right', direction: 'rtl' } : {}}>
                        {selectedIdeaForDetails.goToMarketStrategy}
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Ideas Vault Modal */}
        <AnimatePresence>
          {showIdeasVault && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Backdrop */}
              <motion.div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowIdeasVault(false)}
              />

              {/* Modal */}
              <motion.div
                className={cn(
                  "relative z-10 w-full max-w-4xl max-h-[80vh] rounded-2xl shadow-2xl overflow-hidden",
                  darkMode ? "bg-gray-900" : "bg-white"
                )}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
              >
                {/* Header */}
                <div className={cn(
                  "flex items-center justify-between p-6 border-b",
                  darkMode ? "border-gray-700" : "border-gray-200"
                )}>
                  <h3 className={cn(
                    "text-xl font-bold",
                    darkMode ? "text-white" : "text-gray-900"
                  )}>
                    {i18n.language === 'ar' ? 'اختر فكرة من المخزن' : 'Select an Idea from vault'}
                  </h3>
                  <button
                    onClick={() => setShowIdeasVault(false)}
                    className={cn(
                      "p-2 rounded-lg transition-colors",
                      darkMode
                        ? "text-gray-400 hover:text-white hover:bg-gray-700"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    )}
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Search Input */}
                  <div className="mb-4">
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={i18n.language === 'ar' ? 'ابحث عن الأفكار...' : 'Search ideas...'}
                        className={cn(
                          "w-full px-4 py-2 pl-10 rounded-lg border transition-all",
                          "focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500",
                          darkMode 
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500",
                          "text-start"
                        )}
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <svg 
                          className={cn("h-4 w-4", darkMode ? "text-gray-400" : "text-gray-500")} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {loadingIdeas ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                        <p className={cn("text-sm", darkMode ? "text-gray-400" : "text-gray-600")}>
                          Loading ideas...
                        </p>
                      </div>
                    ) : filteredIdeas.length > 0 ? (
                      filteredIdeas.map((idea, index) => (
                      <motion.div
                        key={idea.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={cn(
                          "p-4 cursor-pointer rounded-xl border-2 transition-all",
                          selectedIdea?.id === idea.id
                            ? "border-blue-500 bg-blue-500/10"
                            : darkMode
                              ? "border-gray-600 bg-gray-700 hover:border-gray-500"
                              : "border-gray-200 bg-white hover:border-gray-300"
                        )}
                        onClick={() => handleIdeaSelect(idea)}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            "bg-gradient-to-r from-blue-500 to-purple-500 text-white"
                          )}>
                            {idea.type}
                          </span>
                          <span className={cn(
                            "text-sm",
                            darkMode ? "text-gray-400" : "text-gray-500"
                          )}>
                            {idea.industry}
                          </span>
                        </div>
                        <h4 className={cn(
                          "text-lg font-bold mb-2",
                          darkMode ? "text-white" : "text-slate-700"
                        )}>
                          {idea.title}
                        </h4>
                        <p className={cn(
                          "text-sm",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )} style={{
                          overflow: 'visible',
                          display: 'block',
                          webkitLineClamp: 'unset',
                          webkitBoxOrient: 'unset',
                          textOverflow: 'unset',
                          whiteSpace: 'normal'
                        }}>
                          {idea.description}
                        </p>
                      </motion.div>
                    ))
                    ) : (
                      <div className="text-center py-8">
                        <p className={cn("text-sm", darkMode ? "text-gray-400" : "text-gray-600")}>
                          {searchQuery.trim() 
                            ? (i18n.language === 'ar' ? 'لم يتم العثور على أفكار مطابقة للبحث' : 'No ideas found matching your search')
                            : (i18n.language === 'ar' ? 'لا توجد أفكار متاحة' : 'No ideas available')
                          }
                        </p>
                        {searchQuery.trim() && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className={cn(
                              "mt-2 text-xs px-3 py-1 rounded-lg transition-colors",
                              darkMode 
                                ? "bg-gray-700 hover:bg-gray-600 text-gray-300"
                                : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                            )}
                          >
                            {i18n.language === 'ar' ? 'مسح البحث' : 'Clear search'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* AI Features - Only show when NOT in Ideas Generator mode */}
        {!isIdeasGeneratorMode && (
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
          <h3 className={cn(
            "text-2xl font-bold mb-6 text-center",
            darkMode ? "text-white" : "text-slate-700"
          )}>
            {t('buildersStudio.aiPoweredFeatures')}
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {aiFeatures.map((feature, index) => (
              <motion.div
                key={feature.label}
                className="relative"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                <motion.button
                  onClick={feature.action}
                  disabled={isGenerating[feature.type] || (user && user.tier === 'free' && (user.credits || 0) <= 0)}
                  className={cn(
                    "flex items-center gap-3 px-6 py-3 rounded-xl font-medium transition-all min-w-[200px] justify-center",
                    isGenerating[feature.type] || (user && user.tier === 'free' && (user.credits || 0) <= 0)
                      ? "cursor-not-allowed opacity-50"
                      : "hover:scale-105 active:scale-95",
                    darkMode 
                      ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg hover:shadow-xl"
                      : "bg-gradient-to-r from-purple-800 to-blue-800 text-white shadow-lg hover:shadow-xl"
                  )}
                  whileHover={!isGenerating[feature.type] && !(user && user.tier === 'free' && (user.credits || 0) <= 0) ? { scale: 1.05 } : {}}
                  whileTap={!isGenerating[feature.type] && !(user && user.tier === 'free' && (user.credits || 0) <= 0) ? { scale: 0.95 } : {}}
                >
                  {isGenerating[feature.type] ? (
                    <Loader className="h-5 w-5 animate-spin" />
                  ) : (
                    feature.icon
                  )}
                  {isGenerating[feature.type] ? t('buildersStudio.generating') : feature.label}
                </motion.button>
                
                {/* Out of credits overlay */}
                {user && user.tier === 'free' && (user.credits || 0) <= 0 && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-xl">
                    <div className="flex items-center gap-2 text-white text-sm font-medium">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      {i18n.language === 'ar' ? 'ترقية مطلوبة' : 'Upgrade Required'}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
          </motion.div>
        )}

        {/* Results Window - Only show when NOT in Ideas Generator mode */}
        {!isIdeasGeneratorMode && (
        <AnimatePresence>
          {activeFeature && generatedContent[activeFeature] && (
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <motion.div
                className={cn(
                  "rounded-2xl shadow-2xl backdrop-blur-xl overflow-hidden max-w-4xl mx-auto",
                  darkMode ? "bg-gray-800/90" : "bg-white/90"
                )}
                initial={{ boxShadow: "0 0 0 0 rgba(147, 51, 234, 0.4)" }}
                animate={{ boxShadow: "0 0 20px 5px rgba(147, 51, 234, 0.3)" }}
                transition={{ duration: 1, repeat: Infinity, repeatType: "reverse" }}
                style={{
                  background: darkMode 
                    ? "linear-gradient(135deg, rgba(31, 41, 55, 0.9) 0%, rgba(55, 65, 81, 0.9) 100%)"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(249, 250, 251, 0.9) 100%)",
                  borderImage: "linear-gradient(135deg, #8b5cf6, #3b82f6) 1"
                }}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={cn(
                      "text-xl font-bold",
                      darkMode ? "text-white" : "text-slate-700"
                    )}>
                      {aiFeatures.find(f => f.type === activeFeature)?.label}
                    </h3>
                    <motion.button
                      onClick={() => setActiveFeature(null)}
                      className={cn(
                        "p-2 rounded-lg transition-colors",
                        darkMode 
                          ? "text-gray-400 hover:text-white hover:bg-gray-700"
                          : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                      )}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <ChevronRight className="h-5 w-5 transform rotate-45" />
                    </motion.button>
                  </div>
                  
                  <div className={cn(
                    "p-4 rounded-xl",
                    darkMode 
                      ? "bg-gray-900/50 border border-gray-700" 
                      : "bg-gray-50 border border-gray-200"
                  )}>
                    {activeFeature === 'landingPage' && generatedContent.landingPage && (
                      <div className="w-full flex justify-center">
                        <CodeBlock 
                          htmlCode={generatedContent.landingPage.htmlCode} 
                          fileName="landing-page.html"
                        />
                      </div>
                    )}
                    
                    {activeFeature === 'marketingStrategy' && generatedContent.marketingStrategy && (
                      <div className="space-y-4">
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Marketing Channels:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-blue-500/30">
                            {Array.isArray(generatedContent.marketingStrategy.channels) 
                              ? generatedContent.marketingStrategy.channels.join('\n\n') 
                              : generatedContent.marketingStrategy.channels}
                          </div>
                        </div>
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Budget:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-green-500/30">
                            {generatedContent.marketingStrategy.budget}
                          </div>
                        </div>
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Timeline:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-purple-500/30">
                            {generatedContent.marketingStrategy.timeline}
                          </div>
                        </div>
                        {generatedContent.marketingStrategy.kpis && (
                          <div className={cn(
                            "text-sm leading-relaxed",
                            darkMode ? "text-gray-300" : "text-slate-600"
                          )}>
                            <div className="font-bold mb-2 text-base">Key Performance Indicators:</div>
                            <div className="whitespace-pre-line pl-2 border-l-2 border-orange-500/30">
                              {Array.isArray(generatedContent.marketingStrategy.kpis) 
                                ? generatedContent.marketingStrategy.kpis.join('\n\n') 
                                : generatedContent.marketingStrategy.kpis}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {activeFeature === 'logo' && generatedContent.logo && (
                      <div className="space-y-4">
                        {/* Brand Names */}
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Suggested Brand Names:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-blue-500/30">
                            {Array.isArray(generatedContent.logo.brandNames) 
                              ? generatedContent.logo.brandNames.join('\n\n') 
                              : generatedContent.logo.brandNames}
                          </div>
                        </div>

                        {/* Design Style */}
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Design Style:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-green-500/30">
                            {generatedContent.logo.designStyle}
                          </div>
                        </div>

                        {/* Logo Concepts Selection */}
                        {generatedContent.logo.concepts && (
                          <div className={cn(
                            "text-sm leading-relaxed",
                            darkMode ? "text-gray-300" : "text-slate-600"
                          )}>
                            <div className="font-bold mb-3 text-base">
                              {i18n.language === 'ar' ? 'اختر مفهوم الشعار:' : 'Select Logo Concept:'}
                            </div>
                            <div className="mt-2 space-y-2">
                              {generatedContent.logo.concepts.map((concept, i) => (
                                <motion.div 
                                  key={i} 
                                  className={cn(
                                    "p-3 rounded-lg cursor-pointer border-2 transition-all",
                                    selectedLogoConcept === concept
                                      ? "border-blue-500 bg-blue-500/10"
                                      : darkMode 
                                        ? "bg-gray-700 border-gray-600 hover:border-gray-500" 
                                        : "bg-gray-100 border-gray-200 hover:border-gray-300"
                                  )}
                                  onClick={() => setSelectedLogoConcept(concept)}
                                  whileHover={{ scale: 1.01 }}
                                  whileTap={{ scale: 0.99 }}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <div className="font-medium">Concept {i + 1}</div>
                                        {selectedLogoConcept === concept && (
                                          <CheckCircle className="h-4 w-4 text-blue-500" />
                                        )}
                                      </div>
                                      <div className="text-xs mb-2">{concept.description}</div>
                                      <div className="text-xs">
                                        <span className="font-medium">Elements:</span> {concept.elements.join(', ')}
                                      </div>
                                      <div className="text-xs">
                                        <span className="font-medium">Typography:</span> {concept.typography}
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                            
                            {/* Generate Logo Button */}
                            <div className="flex justify-center mt-4">
                              <motion.button
                                onClick={generateLogoImages}
                                disabled={!selectedLogoConcept || isGeneratingLogoImages || (user && user.tier === 'free' && (user.credits || 0) <= 0)}
                                className={cn(
                                  "flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all",
                                  !selectedLogoConcept || isGeneratingLogoImages || (user && user.tier === 'free' && (user.credits || 0) <= 0)
                                    ? "cursor-not-allowed opacity-50 bg-gray-400"
                                    : "bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:shadow-lg"
                                )}
                                whileHover={!selectedLogoConcept || isGeneratingLogoImages || (user && user.tier === 'free' && (user.credits || 0) <= 0) ? {} : { scale: 1.05 }}
                                whileTap={!selectedLogoConcept || isGeneratingLogoImages || (user && user.tier === 'free' && (user.credits || 0) <= 0) ? {} : { scale: 0.95 }}
                              >
                                {isGeneratingLogoImages ? (
                                  <>
                                    <Loader className="h-5 w-5 animate-spin" />
                                    {i18n.language === 'ar' ? 'جاري إنشاء الشعار...' : 'Generating Logo...'}
                                  </>
                                ) : (
                                  <>
                                    <Palette className="h-5 w-5" />
                                    {i18n.language === 'ar' ? 'إنشاء الشعار' : 'Generate Logo'}
                                  </>
                                )}
                              </motion.button>
                            </div>
                          </div>
                        )}

                        {/* Generated Logo Images */}
                        {isGeneratingLogoImages && (
                          <div className={cn(
                            "text-sm leading-relaxed",
                            darkMode ? "text-gray-300" : "text-slate-600"
                          )}>
                            <div className="font-bold mb-3 text-base">
                              {i18n.language === 'ar' ? 'جاري إنشاء الصور...' : 'Generating Images...'}
                            </div>
                            <div className="flex justify-center items-center py-8">
                              <div className="flex flex-col items-center gap-4">
                                <Loader className="h-8 w-8 animate-spin text-blue-500" />
                                <p className="text-sm text-center max-w-md">
                                  {i18n.language === 'ar' 
                                    ? 'يتم إنشاء شعارات احترافية باستخدام الذكاء الاصطناعي. قد تستغرق هذه العملية بضع دقائق...'
                                    : 'Creating professional logos using AI. This process may take a few minutes...'
                                  }
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {generatedLogoImages.length > 0 && !isGeneratingLogoImages && (
                          <div className={cn(
                            "text-sm leading-relaxed",
                            darkMode ? "text-gray-300" : "text-slate-600"
                          )}>
                            <div className="font-bold mb-3 text-base">
                              {i18n.language === 'ar' ? 'الصور المُولدة:' : 'Generated Logos:'}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {generatedLogoImages.map((logo, i) => (
                                <motion.div
                                  key={logo.leonardoId || i}
                                  className={cn(
                                    "p-4 rounded-lg border",
                                    darkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"
                                  )}
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: i * 0.1 }}
                                >
                                  <div className="aspect-square mb-3 rounded-lg overflow-hidden bg-white flex items-center justify-center shadow-inner">
                                    <img 
                                      src={logo.base64} 
                                      alt={`Logo ${logo.index || i + 1}`}
                                      className="max-w-full max-h-full object-contain"
                                      onError={(e) => {
                                        console.error('Failed to load logo image:', e);
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="flex flex-col">
                                      <span className="text-xs font-medium">Logo {logo.index || i + 1}</span>
                                    </div>
                                    <motion.button
                                      onClick={() => downloadLogo(logo, i)}
                                      className={cn(
                                        "flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium transition-colors",
                                        darkMode 
                                          ? "bg-blue-600 hover:bg-blue-700 text-white"
                                          : "bg-blue-500 hover:bg-blue-600 text-white"
                                      )}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                    >
                                      <Download className="h-3 w-3" />
                                      {i18n.language === 'ar' ? 'تحميل' : 'Download'}
                                    </motion.button>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Color Palette */}
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Suggested Colors:</div>
                          <div className="pl-2 border-l-2 border-pink-500/30">
                            <div className="flex gap-2 mt-2">
                            {generatedContent.logo.suggestedColors.map((color, i) => (
                              <motion.div
                                key={i}
                                className="w-6 h-6 rounded-full border-2 border-white shadow-lg"
                                style={{ backgroundColor: color }}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: i * 0.1 }}
                                title={color}
                              />
                            ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {activeFeature === 'marketAnalysis' && generatedContent.marketAnalysis && (
                      <div className="space-y-4">
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Market Size:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-blue-500/30">
                            {generatedContent.marketAnalysis.marketSize}
                          </div>
                        </div>
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Growth Rate:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-green-500/30">
                            {generatedContent.marketAnalysis.growth}
                          </div>
                        </div>
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Competitors:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-purple-500/30">
                            {Array.isArray(generatedContent.marketAnalysis.competitors) 
                              ? generatedContent.marketAnalysis.competitors.join('\n\n') 
                              : generatedContent.marketAnalysis.competitors}
                          </div>
                        </div>
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Opportunities:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-orange-500/30">
                            {Array.isArray(generatedContent.marketAnalysis.opportunities) 
                              ? generatedContent.marketAnalysis.opportunities.join('\n\n') 
                              : generatedContent.marketAnalysis.opportunities}
                          </div>
                        </div>
                        {generatedContent.marketAnalysis.threats && (
                          <div className={cn(
                            "text-sm leading-relaxed",
                            darkMode ? "text-gray-300" : "text-slate-600"
                          )}>
                            <div className="font-bold mb-2 text-base">Threats:</div>
                            <div className="whitespace-pre-line pl-2 border-l-2 border-red-500/30">
                              {Array.isArray(generatedContent.marketAnalysis.threats) 
                                ? generatedContent.marketAnalysis.threats.join('\n\n') 
                                : generatedContent.marketAnalysis.threats}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {activeFeature === 'technicalGuidance' && generatedContent.technicalGuidance && (
                      <div className="space-y-4">
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Tech Stack:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-blue-500/30">
                            {Array.isArray(generatedContent.technicalGuidance.stack) 
                              ? generatedContent.technicalGuidance.stack.join('\n\n') 
                              : generatedContent.technicalGuidance.stack}
                          </div>
                        </div>
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Timeline:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-green-500/30">
                            {generatedContent.technicalGuidance.timeline}
                          </div>
                        </div>
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Team:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-purple-500/30">
                            {Array.isArray(generatedContent.technicalGuidance.team) 
                              ? generatedContent.technicalGuidance.team.join('\n\n') 
                              : generatedContent.technicalGuidance.team}
                          </div>
                        </div>
                        <div className={cn(
                          "text-sm leading-relaxed",
                          darkMode ? "text-gray-300" : "text-slate-600"
                        )}>
                          <div className="font-bold mb-2 text-base">Recommendations:</div>
                          <div className="whitespace-pre-line pl-2 border-l-2 border-orange-500/30">
                            {Array.isArray(generatedContent.technicalGuidance.recommendations) 
                              ? generatedContent.technicalGuidance.recommendations.join('\n\n') 
                              : generatedContent.technicalGuidance.recommendations}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        )}

        {/* Call to Action - Only show when NOT in Ideas Generator mode */}
        {!isIdeasGeneratorMode && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            <div className={cn(
            "p-8 rounded-2xl border shadow-xl backdrop-blur-xl text-center",
            darkMode ? "border-gray-700 bg-gray-800/80" : "border-gray-200 bg-white/80"
          )}>
            <h3 className={cn(
              "text-2xl font-bold mb-4",
              darkMode ? "text-white" : "text-slate-700",
"text-start"
            )}>
              {t('buildersStudio.needMoreIdeas')}
            </h3>
            <p className={cn(
              "mb-6",
              darkMode ? "text-gray-300" : "text-slate-600"
            )}>
              {t('buildersStudio.exploreVault')}
            </p>
            <Link to="/ideas-vault">
              <motion.button
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('buildersStudio.visitIdeasVault')}
                <ChevronRight className="h-4 w-4" />
              </motion.button>
            </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BuildersStudio;
